import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapPin, Clock, Car, Copy, Share2, AlertTriangle, Calculator, Navigation, MapPinOff } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useGeolocation } from '../hooks/useGeolocation';

// Custom user location icon (blue circle with pulse)
const userIcon = new L.DivIcon({
  className: 'custom-div-icon',
  html: `
    <div class="relative">
      <div style="
        background-color: #3b82f6;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 3px solid #ffffff;
        box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
      "></div>
      <div style="
        position: absolute;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        border-radius: 50%;
        background: #3b82f6;
        opacity: 0.3;
        animation: pulse 2s infinite;
      "></div>
    </div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

// Custom destination icon
const destinationIcon = new L.DivIcon({
  className: 'custom-div-icon',
  html: `
    <div style="
      background-color: #ef4444;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 3px solid #ffffff;
      box-shadow: 0 0 15px rgba(239, 68, 68, 0.6);
    "></div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

// 2025 Şehir bazlı taksimetre ücretleri
const cityFares = {
  // İstanbul (34)
  34: {
    city: "İstanbul",
    basePrice: 42.00,
    perKm: 28.00,
    minPrice: 135.00,
    waitingPrice: 5.83
  },
  // Ankara (6)
  6: {
    city: "Ankara",
    basePrice: 35.00,
    perKm: 25.00,
    minPrice: 100.00,
    waitingPrice: 5.83
  },
  // İzmir (35)
  35: {
    city: "İzmir",
    basePrice: 20.00,
    perKm: 30.00,
    minPrice: 100.00,
    waitingPrice: 5.83
  },
  // Diğer tüm iller için varsayılan tarife
  default: {
    city: "Diğer İller",
    basePrice: 40.00,
    perKm: 30.00,
    minPrice: 100.00,
    waitingPrice: 5.83
  }
};

// Trafik durumları
const trafficLevels = {
  LOW: 'Normal Trafik',
  MEDIUM: 'Orta Yoğunlukta Trafik',
  HIGH: 'Yoğun Trafik'
};

// Map Updater Component
function MapUpdater({ bounds }: { bounds?: L.LatLngBounds }) {
  const map = useMap();
  
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  
  return null;
}

interface NominatimResult {
  lat: string;
  lon: string;
  display_name: string;
  address: {
    city?: string;
    state?: string;
  };
}

// Rota türleri için interface
interface Route {
  coordinates: [number, number][];
  distance: number;
  duration: number;
  fare: number;
  trafficLevel: string;
  description: string;
}

// Harita Tıklama Bileşeni
function MapClickHandler({ onLocationSelect }: { onLocationSelect: (lat: number, lng: number) => void }) {
  const [isSelectingLocation, setIsSelectingLocation] = useState(false);
  
  const map = useMapEvents({
    click(e) {
      if (isSelectingLocation) {
        onLocationSelect(e.latlng.lat, e.latlng.lng);
        setIsSelectingLocation(false);
        map.dragging.enable();
      }
    }
  });

  useEffect(() => {
    if (isSelectingLocation) {
      map.dragging.disable();
    } else {
      map.dragging.enable();
    }
  }, [isSelectingLocation, map]);

  return (
    <div className="absolute bottom-4 left-4 z-[1000]">
      <button
        onClick={() => setIsSelectingLocation(!isSelectingLocation)}
        className={`p-4 rounded-xl shadow-lg transition-colors flex items-center gap-2 ${
          isSelectingLocation
            ? 'bg-yellow-400 text-black'
            : 'bg-yellow-400/90 text-black hover:bg-yellow-400'
        }`}
        title={isSelectingLocation ? 'İptal Et' : 'Haritadan Seç'}
      >
        {isSelectingLocation ? (
          <>
            <MapPinOff className="w-6 h-6" />
            <span className="font-medium">İptal Et</span>
          </>
        ) : (
          <>
            <MapPin className="w-6 h-6" />
            <span className="font-medium">Haritadan Seç</span>
          </>
        )}
      </button>
      {isSelectingLocation && (
        <div className="absolute bottom-16 left-0 bg-[#24272e] text-white/80 text-sm px-4 py-2 rounded-xl whitespace-nowrap border border-white/5">
          <p className="text-sm">Haritada istediğiniz varış noktasına tıklayarak seçim yapın, ve hesaplayın.</p>
        </div>
      )}
    </div>
  );
}

export default function FareCalculator() {
  const startInputRef = useRef<HTMLInputElement>(null);
  const endInputRef = useRef<HTMLInputElement>(null);
  
  const [startLocation, setStartLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [endLocation, setEndLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [routeCoordinates, setRouteCoordinates] = useState<[number, number][]>([]);
  const [mapBounds, setMapBounds] = useState<L.LatLngBounds | undefined>(undefined);
  const [distance, setDistance] = useState<number | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [fare, setFare] = useState<number | null>(null);
  const [trafficInfo, setTrafficInfo] = useState<string>(trafficLevels.LOW);
  const [error, setError] = useState<string | null>(null);
  const [mapUrl, setMapUrl] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string>("İstanbul");
  const [loading, setLoading] = useState(false);

  // Yeni state'ler
  const [startSuggestions, setStartSuggestions] = useState<NominatimResult[]>([]);
  const [endSuggestions, setEndSuggestions] = useState<NominatimResult[]>([]);
  const [isStartSearching, setIsStartSearching] = useState(false);
  const [isEndSearching, setIsEndSearching] = useState(false);
  const [startQuery, setStartQuery] = useState("");
  const [endQuery, setEndQuery] = useState("");

  const { location: userLocation, loading: locationLoading, getLocation } = useGeolocation();

  const [routes, setRoutes] = useState<Route[]>([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState<number>(0);

  const [isSimulating, setIsSimulating] = useState(false);
  const [currentPosition, setCurrentPosition] = useState<number>(0);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [currentFare, setCurrentFare] = useState<number>(0);
  const [coveredDistance, setCoveredDistance] = useState<number>(0);

  // Debounce fonksiyonu
  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Nominatim API ile adres arama
  const searchAddress = async (query: string): Promise<NominatimResult[]> => {
    if (!query.trim()) return [];
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=tr&limit=5`
      );
      const data = await response.json();
      return data || [];
    } catch (err) {
      console.error('Address search error:', err);
      return [];
    }
  };

  // Debounce edilmiş arama fonksiyonları
  const debouncedStartSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        if (query.length < 3) {
          setStartSuggestions([]);
          return;
        }
        setIsStartSearching(true);
        const results = await searchAddress(query);
        setStartSuggestions(results);
        setIsStartSearching(false);
      }, 300),
    []
  );

  const debouncedEndSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        if (query.length < 3) {
          setEndSuggestions([]);
          return;
        }
        setIsEndSearching(true);
        const results = await searchAddress(query);
        setEndSuggestions(results);
        setIsEndSearching(false);
      }, 300),
    []
  );

  // Input değişiklik handler'ları
  const handleStartInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStartQuery(value);
    debouncedStartSearch(value);
  };

  const handleEndInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEndQuery(value);
    debouncedEndSearch(value);
  };

  // Öneri seçme handler'ları
  const handleStartSuggestionSelect = (suggestion: NominatimResult) => {
    setStartQuery(suggestion.display_name);
    setStartLocation({ lat: parseFloat(suggestion.lat), lng: parseFloat(suggestion.lon) });
    setStartSuggestions([]);
    if (suggestion.address?.city) {
      setSelectedCity(suggestion.address.city);
    } else if (suggestion.address?.state) {
      setSelectedCity(suggestion.address.state);
    }
  };

  const handleEndSuggestionSelect = (suggestion: NominatimResult) => {
    setEndQuery(suggestion.display_name);
    setEndLocation({ lat: parseFloat(suggestion.lat), lng: parseFloat(suggestion.lon) });
    setEndSuggestions([]);
  };

  // OSRM API ile rota hesaplama
  const getRoute = async (start: [number, number], end: [number, number]) => {
    try {
      const response = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${start[1]},${start[0]};${end[1]},${end[0]}?overview=full&geometries=geojson&alternatives=true&steps=true`
      );
      const data = await response.json();
      
      if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
        throw new Error('Route not found');
      }

      const processedRoutes: Route[] = data.routes.map((route: any, index: number) => {
        const coordinates = route.geometry.coordinates.map((coord: number[]) => [coord[1], coord[0]] as [number, number]);
        const distanceInKm = route.distance / 1000;
        const durationInMinutes = route.duration / 60;
        
        // Trafik durumunu hesapla
        const trafficRatio = durationInMinutes / (distanceInKm * 1.2);
        const trafficLevel = trafficRatio > 2 ? trafficLevels.HIGH : 
                           trafficRatio > 1.5 ? trafficLevels.MEDIUM : 
                           trafficLevels.LOW;

        // Ücreti hesapla
        const cityFare = cityFares[selectedCity === "İstanbul" ? 34 : selectedCity === "Ankara" ? 6 : selectedCity === "İzmir" ? 35 : "default"];
        const baseFare = cityFare.basePrice;
        const distanceFare = distanceInKm * cityFare.perKm;
        const waitingFare = (durationInMinutes * cityFare.waitingPrice) / 60;
        const totalFare = Math.max(
          cityFare.minPrice,
          baseFare + distanceFare + Math.max(0, waitingFare)
        );

        // Rota açıklaması oluştur
        let description = index === 0 ? "En Hızlı Rota" : 
                         index === 1 ? "Alternatif Rota 1" : 
                         `Alternatif Rota ${index}`;

        if (route.legs[0]?.steps) {
          const mainRoads = route.legs[0].steps
            .filter((step: any) => step.name && !step.name.includes("unnamed"))
            .map((step: any) => step.name)
            .filter((name: string, i: number, arr: string[]) => arr.indexOf(name) === i)
            .slice(0, 2)
            .join(" → ");
          
          if (mainRoads) {
            description += ` (${mainRoads})`;
          }
        }

        return {
          coordinates,
          distance: distanceInKm,
          duration: durationInMinutes,
          fare: totalFare,
          trafficLevel,
          description
        };
      });

      return processedRoutes;
    } catch (err) {
      console.error('Route calculation error:', err);
      throw err;
    }
  };

  const calculateRoute = async () => {
    if (!startInputRef.current?.value || !endInputRef.current?.value) {
      setError('Lütfen başlangıç ve varış noktalarını belirtin.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Başlangıç noktasını ara
      const startResult = await searchAddress(startInputRef.current.value);
      if (!startResult.length) {
        throw new Error('Başlangıç adresi bulunamadı.');
      }

      // Varış noktasını ara
      const endResult = await searchAddress(endInputRef.current.value);
      if (!endResult.length) {
        throw new Error('Varış adresi bulunamadı.');
      }

      // Koordinatları ayarla
      const start: [number, number] = [parseFloat(startResult[0].lat), parseFloat(startResult[0].lon)];
      const end: [number, number] = [parseFloat(endResult[0].lat), parseFloat(endResult[0].lon)];

      setStartLocation({ lat: start[0], lng: start[1] });
      setEndLocation({ lat: end[0], lng: end[1] });

      // Şehir bilgisini güncelle
      if (startResult[0].address?.city) {
        setSelectedCity(startResult[0].address.city);
      } else if (startResult[0].address?.state) {
        setSelectedCity(startResult[0].address.state);
      }

      // Rotaları hesapla
      const calculatedRoutes = await getRoute(start, end);
      setRoutes(calculatedRoutes);
      setSelectedRouteIndex(0);

      // İlk rotayı seç ve state'leri güncelle
      const selectedRoute = calculatedRoutes[0];
      setRouteCoordinates(selectedRoute.coordinates);
      setDistance(selectedRoute.distance);
      setDuration(selectedRoute.duration);
      setTrafficInfo(selectedRoute.trafficLevel);
      setFare(selectedRoute.fare);

      // Harita sınırlarını ayarla
      const bounds = L.latLngBounds(selectedRoute.coordinates);
      setMapBounds(bounds);

      setMapUrl(`https://www.openstreetmap.org/directions?from=${start[0]},${start[1]}&to=${end[0]},${end[1]}`);

    } catch (err) {
      console.error('Route calculation error:', err);
      setError(err instanceof Error ? err.message : 'Rota hesaplanamadı.');
    } finally {
      setLoading(false);
    }
  };

  // Rota seçimi için handler
  const handleRouteSelect = (index: number) => {
    setSelectedRouteIndex(index);
    const selectedRoute = routes[index];
    setRouteCoordinates(selectedRoute.coordinates);
    setDistance(selectedRoute.distance);
    setDuration(selectedRoute.duration);
    setTrafficInfo(selectedRoute.trafficLevel);
    setFare(selectedRoute.fare);
    
    const bounds = L.latLngBounds(selectedRoute.coordinates);
    setMapBounds(bounds);
  };

  const handleUseMyLocation = async () => {
    if (locationLoading) return;
    
    try {
      const location = await getLocation();
      if (location && 'latitude' in location && 'longitude' in location) {
        // Reverse geocoding ile adresi bul
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.latitude}&lon=${location.longitude}`
        );
        const data = await response.json();
        
        if (startInputRef.current) {
          startInputRef.current.value = data.display_name;
          setStartQuery(data.display_name);
        }
        
        const newLocation = {
          lat: location.latitude,
          lng: location.longitude
        };
        
        setStartLocation(newLocation);

        // Harita sınırlarını güncelle ve konuma odaklan
        const bounds = L.latLngBounds([
          [newLocation.lat - 0.01, newLocation.lng - 0.01],
          [newLocation.lat + 0.01, newLocation.lng + 0.01]
        ]);
        setMapBounds(bounds);

        if (data.address?.city) {
          setSelectedCity(data.address.city);
        } else if (data.address?.state) {
          setSelectedCity(data.address.state);
        }
      }
    } catch (err) {
      setError('Konum alınamadı. Lütfen konum iznini kontrol edin.');
    }
  };

  const copyMapUrl = () => {
    if (mapUrl) {
      navigator.clipboard.writeText(mapUrl);
    }
  };

  const shareRoute = () => {
    if (mapUrl) {
      if (navigator.share) {
        navigator.share({
          title: 'Taksi Rotası',
          text: 'Taksi rotamı kontrol et!',
          url: mapUrl
        });
      }
    }
  };

  // Rota simülasyonu için timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isSimulating && routes[selectedRouteIndex]) {
      const route = routes[selectedRouteIndex];
      const totalPoints = route.coordinates.length;
      const simulationDuration = route.duration * 60; // dakikayı saniyeye çevir
      const updateInterval = 1000; // her saniye güncelle
      
      interval = setInterval(() => {
        setCurrentPosition(prev => {
          const next = prev + (totalPoints / simulationDuration);
          return next >= totalPoints ? totalPoints - 1 : next;
        });
        
        setElapsedTime(prev => {
          const next = prev + (updateInterval / 1000);
          return next >= route.duration * 60 ? route.duration * 60 : next;
        });
        
        // Katedilen mesafeyi hesapla
        const progress = Math.min(elapsedTime / (route.duration * 60), 1);
        const distance = route.distance * progress;
        setCoveredDistance(distance);
        
        // Anlık ücreti hesapla
        const cityFare = cityFares[selectedCity === "İstanbul" ? 34 : selectedCity === "Ankara" ? 6 : selectedCity === "İzmir" ? 35 : "default"];
        const baseFare = cityFare.basePrice;
        const distanceFare = distance * cityFare.perKm;
        const waitingFare = (elapsedTime / 60) * cityFare.waitingPrice;
        const fare = Math.max(
          cityFare.minPrice,
          baseFare + distanceFare + Math.max(0, waitingFare)
        );
        setCurrentFare(fare);
        
        // Simülasyonu durdur
        if (elapsedTime >= route.duration * 60) {
          setIsSimulating(false);
        }
      }, updateInterval);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isSimulating, routes, selectedRouteIndex, elapsedTime, selectedCity]);

  const startSimulation = () => {
    setCurrentPosition(0);
    setElapsedTime(0);
    setCurrentFare(0);
    setCoveredDistance(0);
    setIsSimulating(true);
  };

  const stopSimulation = () => {
    setIsSimulating(false);
  };

  const resetSimulation = () => {
    setIsSimulating(false);
    setCurrentPosition(0);
    setElapsedTime(0);
    setCurrentFare(0);
    setCoveredDistance(0);
  };

  // Haritadan konum seçme fonksiyonu
  const handleMapLocationSelect = (lat: number, lng: number) => {
    // Reverse geocoding ile adresi bul
    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`)
      .then(response => response.json())
      .then(data => {
        setEndQuery(data.display_name);
        setEndLocation({ lat, lng });
        if (endInputRef.current) {
          endInputRef.current.value = data.display_name;
        }
      })
      .catch(error => {
        console.error('Reverse geocoding error:', error);
        setError('Adres bilgisi alınamadı.');
      });
  };

  return (
    <div className="container mx-auto px-4">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Sol Kolon - Form */}
          <div className="space-y-6">
            <div className="bg-[#24272e] rounded-2xl p-6 shadow-xl border border-white/10">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl shadow-lg shadow-yellow-400/20">
                  <Calculator className="w-8 h-8 text-black" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Taksi Ücreti Hesapla</h1>
                  <p className="text-white/60 mt-1">Yolculuk ücretinizi önceden öğrenin</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex flex-col space-y-2">
                    <input
                      ref={startInputRef}
                      type="text"
                      placeholder="Nereden?"
                      value={startQuery}
                      className="w-full bg-[#1a1d24] text-white px-4 py-3 rounded-xl border border-white/10 focus:border-yellow-400/50 focus:ring-2 focus:ring-yellow-400/20 focus:outline-none placeholder-white/30"
                      onChange={handleStartInputChange}
                    />
                    {isStartSearching && (
                      <div className="mt-2 text-sm text-white/60">
                        Aranıyor...
                      </div>
                    )}
                    {startSuggestions.length > 0 && (
                      <div className="absolute z-50 mt-1 w-full bg-[#24272e] rounded-xl border border-white/10 shadow-xl">
                        <div className="py-1">
                          {startSuggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              className="w-full px-4 py-2 text-left text-sm text-white hover:bg-[#2d3039] focus:outline-none"
                              onClick={() => handleStartSuggestionSelect(suggestion)}
                            >
                              {suggestion.display_name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <button
                      onClick={handleUseMyLocation}
                      disabled={locationLoading}
                      className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 text-sm font-medium px-2 py-1 transition-colors"
                    >
                      {locationLoading ? (
                        <>
                          <Clock className="w-4 h-4 animate-spin" />
                          <span>Konum Alınıyor...</span>
                        </>
                      ) : (
                        <>
                          <Navigation className="w-4 h-4" />
                          <span>Konumumu Kullan</span>
                        </>
                      )}
                    </button>
                  </div>

                  <div className="relative">
                    <input
                      ref={endInputRef}
                      type="text"
                      placeholder="Nereye?"
                      value={endQuery}
                      className="w-full bg-[#1a1d24] text-white px-4 py-3 rounded-xl border border-white/10 focus:border-yellow-400/50 focus:ring-2 focus:ring-yellow-400/20 focus:outline-none placeholder-white/30 mt-4"
                      onChange={handleEndInputChange}
                    />
                    <button
                      onClick={() => {
                        const mapElement = document.querySelector('.leaflet-container');
                        if (mapElement) {
                          mapElement.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1a1d24] hover:bg-[#2d3039] text-white rounded-lg transition-colors"
                    >
                      <MapPin className="w-5 h-5 text-yellow-400" />
                      <span>Haritadan Seç</span>
                    </button>
                  </div>
                  {isEndSearching && (
                    <div className="mt-2 text-sm text-white/60">
                      Aranıyor...
                    </div>
                  )}
                  {endSuggestions.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full bg-[#24272e] rounded-xl border border-white/10 shadow-xl">
                      <div className="py-1">
                        {endSuggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            className="w-full px-4 py-2 text-left text-sm text-white hover:bg-[#2d3039] focus:outline-none"
                            onClick={() => handleEndSuggestionSelect(suggestion)}
                          >
                            {suggestion.display_name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={calculateRoute}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-semibold rounded-xl transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Clock className="w-5 h-5 animate-spin" />
                      <span>Hesaplanıyor...</span>
                    </>
                  ) : (
                    <>
                      <Calculator className="w-5 h-5" />
                      <span>Ücreti Hesapla</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-6 py-4 rounded-xl flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <p>{error}</p>
              </div>
            )}

            {/* Harita */}
            <div className="h-[400px] rounded-xl overflow-hidden border border-white/10 shadow-lg bg-[#24272e] relative mt-4">
              <MapContainer
                center={[41.0082, 28.9784]}
                zoom={11}
                style={{ height: '100%', width: '100%' }}
                className="z-10"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png"
                />

                <MapClickHandler onLocationSelect={handleMapLocationSelect} />

                {mapBounds && <MapUpdater bounds={mapBounds} />}

                {startLocation && (
                  <Marker
                    position={[startLocation.lat, startLocation.lng]}
                    icon={userIcon}
                  >
                    <Popup>Başlangıç Noktası</Popup>
                   </Marker>
                )}

                {endLocation && (
                  <Marker
                    position={[endLocation.lat, endLocation.lng]}
                    icon={destinationIcon}
                  >
                    <Popup>Varış Noktası</Popup>
                  </Marker>
                )}

                {routeCoordinates.length > 0 && (
                  <Polyline
                    positions={routeCoordinates}
                    color="#facc15"
                    weight={4}
                    opacity={0.8}
                  />
                )}
              </MapContainer>
            </div>

            {/* Alternatif Rotalar */}
            {routes.length > 0 && (
              <div className="bg-[#24272e] rounded-xl p-4 border border-white/10 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Mevcut Rotalar</h3>
                  <span className="text-sm text-white/60">{routes.length} rota bulundu</span>
                </div>
                <div className="grid gap-2">
                  {routes.map((route, index) => (
                    <button
                      key={index}
                      onClick={() => handleRouteSelect(index)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                        selectedRouteIndex === index
                          ? 'bg-yellow-400/20 border border-yellow-400/30'
                          : 'bg-[#1a1d24] hover:bg-[#2d3039] border border-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          selectedRouteIndex === index ? 'bg-yellow-400/20' : 'bg-[#2d3039]'
                        }`}>
                          <Car className={`w-4 h-4 ${
                            selectedRouteIndex === index ? 'text-yellow-400' : 'text-white/60'
                          }`} />
                        </div>
                        <div className="text-left">
                          <div className={`font-medium ${
                            selectedRouteIndex === index ? 'text-yellow-400' : 'text-white'
                          }`}>
                            {route.description}
                          </div>
                          <div className="text-sm text-white/60">
                            {Math.round(route.duration)} dk • {route.distance.toFixed(1)} km
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-medium ${
                          selectedRouteIndex === index ? 'text-yellow-400' : 'text-white'
                        }`}>
                          ₺{route.fare.toFixed(2)}
                        </div>
                        <div className={`text-sm ${
                          route.trafficLevel.includes('Yoğun')
                            ? 'text-red-400'
                            : route.trafficLevel.includes('Orta')
                            ? 'text-yellow-400'
                            : 'text-green-400'
                        }`}>
                          {route.trafficLevel}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sonuç Raporu */}
            {routes.length > 0 && routes[selectedRouteIndex] && (
              <div className="space-y-6">
                {/* Ana Metrikler */}
                <div className="bg-gradient-to-br from-[#24272e] to-[#1a1d24] rounded-xl shadow-xl border border-white/10">
                  <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
                    {/* Mesafe */}
                    <div className="p-4 sm:p-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-400/10">
                          <MapPin className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <div className="text-sm text-blue-400 mb-0.5">Toplam Mesafe</div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-bold text-white">{routes[selectedRouteIndex].distance.toFixed(1)}</span>
                            <span className="text-sm text-white/60">km</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Süre */}
                    <div className="p-4 sm:p-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-400/10">
                          <Clock className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <div className="text-sm text-purple-400 mb-0.5">Tahmini Süre</div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-bold text-white">{Math.round(routes[selectedRouteIndex].duration)}</span>
                            <span className="text-sm text-white/60">dk</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Ücret */}
                    <div className="p-4 sm:p-6 bg-yellow-400/5">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-yellow-400/10">
                          <Car className="w-5 h-5 text-yellow-400" />
                        </div>
                        <div>
                          <div className="text-sm text-yellow-400 mb-0.5">Toplam Ücret</div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-bold text-yellow-400">₺{routes[selectedRouteIndex].fare.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detaylı Bilgiler */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Trafik ve Varış Bilgisi */}
                  <div className="bg-[#24272e] rounded-xl p-6 border border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-4">Trafik ve Varış</h3>
                    <div className="space-y-4">
                      {/* Trafik Durumu */}
                      <div
                        className={`flex items-center gap-3 p-3 rounded-lg ${
                          routes[selectedRouteIndex].trafficLevel.includes('Yoğun')
                            ? 'bg-red-500/10 border border-red-500/20'
                            : routes[selectedRouteIndex].trafficLevel.includes('Orta')
                            ? 'bg-yellow-500/10 border border-yellow-500/20'
                            : 'bg-green-500/10 border border-green-500/20'
                        }`}
                      >
                        <Clock
                          className={`w-5 h-5 ${
                            routes[selectedRouteIndex].trafficLevel.includes('Yoğun')
                              ? 'text-red-400'
                              : routes[selectedRouteIndex].trafficLevel.includes('Orta')
                              ? 'text-yellow-400'
                              : 'text-green-400'
                          }`}
                        />
                        <div>
                          <div className="font-medium text-white">{routes[selectedRouteIndex].trafficLevel}</div>
                          <div className="text-sm text-white/60">
                            Tahmini varış:{' '}
                            {new Date(Date.now() + routes[selectedRouteIndex].duration * 60000).toLocaleTimeString('tr-TR', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Rota Detayları */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center p-2 bg-[#1a1d24] rounded-lg">
                          <span className="text-white/60">Sürüş Süresi</span>
                          <span className="font-medium text-white">{Math.round(routes[selectedRouteIndex].duration)} dk</span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-[#1a1d24] rounded-lg">
                          <span className="text-white/60">Ortalama Hız</span>
                          <span className="font-medium text-white">
                            {Math.round((routes[selectedRouteIndex].distance / (routes[selectedRouteIndex].duration / 60)) * 10) / 10} km/s
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ücret Detayları */}
                  <div className="bg-[#24272e] rounded-xl p-6 border border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-4">
                      {selectedCity} Taksi Ücret Detayları
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-2 bg-[#1a1d24] rounded-lg">
                        <span className="text-white/60">Taksimetre Açılış</span>
                        <span className="font-medium text-white">
                          ₺{selectedCity === "İstanbul"
                            ? "42.00"
                            : selectedCity === "Ankara"
                            ? "35.00"
                            : selectedCity === "İzmir"
                            ? "20.00"
                            : "40.00"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-2 bg-[#1a1d24] rounded-lg">
                        <span className="text-white/60">KM Başına</span>
                        <span className="font-medium text-white">
                          ₺{selectedCity === "İstanbul"
                            ? "28.00"
                            : selectedCity === "Ankara"
                            ? "25.00"
                            : selectedCity === "İzmir"
                            ? "30.00"
                            : "30.00"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-2 bg-[#1a1d24] rounded-lg">
                        <span className="text-white/60">Bekleme Ücreti</span>
                        <span className="font-medium text-white">₺5.83/dk</span>
                      </div>
                      <div className="flex justify-between items-center p-2 bg-[#1a1d24] rounded-lg">
                        <span className="text-white/60">İndi-Bindi Ücreti</span>
                        <span className="font-medium text-white">
                          ₺{selectedCity === "İstanbul" ? "135.00" : "100.00"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Paylaşım Butonları */}
                {mapUrl && (
                  <div className="bg-[#24272e] rounded-xl p-4 border border-white/10">
                    <div className="flex gap-2">
                      <button
                        onClick={copyMapUrl}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#1a1d24] hover:bg-[#2d3039] text-white rounded-lg transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                        Rotayı Kopyala
                      </button>
                      <button
                        onClick={shareRoute}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#1a1d24] hover:bg-[#2d3039] text-white rounded-lg transition-colors"
                      >
                        <Share2 className="w-4 h-4" />
                        Paylaş
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sağ Kolon */}
          <div className="lg:sticky lg:top-8">
            {/* Buraya başka bir içerik eklenebilir */}
          </div>
        </div>
      </div>
    </div>
  );
}